import React from 'react';
import { Card, Typography, Tag, Space, Button, Collapse } from 'antd';
import { ReloadOutlined, CopyOutlined } from '@ant-design/icons';
import { ScriptTag } from '../ScriptTag';
import { SellingPointFABE } from '../../../types';

const { Text, Paragraph } = Typography;

const FABE_LABELS = {
    feature: { label: '特性 (F)', color: '#1890ff', desc: '产品功能特点' },
    advantage: { label: '优势 (A)', color: '#52c41a', desc: '相比竞品的优势' },
    benefit: { label: '利益 (B)', color: '#faad14', desc: '给用户带来的好处' },
    evidence: { label: '证据 (E)', color: '#eb2f96', desc: '支持以上的证据' },
} as const;

interface SellingPointCardProps {
    fabe: SellingPointFABE;
    index: number;
    bulletPoint: string;
    activeSite: string;
    regeneratingFABE?: string | null;
    onRegenerateFABE?: (sellingPoint: string) => Promise<void>;
    copyToClipboard: (text: string) => void;
}

export const SellingPointCard: React.FC<SellingPointCardProps> = ({
    fabe,
    index,
    bulletPoint,
    activeSite,
    regeneratingFABE,
    onRegenerateFABE,
    copyToClipboard,
}) => {
    const isRegenerating = regeneratingFABE === fabe.selling_point;

    return (
        <Card
            key={index}
            size="small"
            style={{ marginBottom: 16 }}
            title={
                <Space>
                    <Tag color="blue">卖点 {index + 1}</Tag>
                    <ScriptTag type={fabe.script_used} />
                    <Text strong>{fabe.selling_point}</Text>
                </Space>
            }
            extra={
                onRegenerateFABE && (
                    <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={isRegenerating}
                        onClick={() => onRegenerateFABE(fabe.selling_point)}
                    >
                        重写
                    </Button>
                )
            }
        >
            {/* Bullet Point 文案 */}
            <div style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
                padding: 12,
                marginBottom: 16
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <Tag color="green" style={{ marginBottom: 8 }}>Bullet Point ({activeSite})</Tag>
                        <Paragraph style={{ margin: 0, fontSize: 14 }}>
                            {bulletPoint || <Text type="secondary">暂无文案</Text>}
                        </Paragraph>
                    </div>
                    <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(bulletPoint)}
                    />
                </div>
            </div>

            {/* FABE 分析 - 折叠面板 */}
            <Collapse
                size="small"
                items={[{
                    key: 'fabe',
                    label: <Text strong>📊 FABE 分析详情</Text>,
                    children: (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {(Object.keys(FABE_LABELS) as Array<keyof typeof FABE_LABELS>).map(key => (
                                <div key={key} style={{ padding: 8, background: '#fafafa', borderRadius: 4 }}>
                                    <Tag color={FABE_LABELS[key].color} style={{ marginBottom: 4 }}>
                                        {FABE_LABELS[key].label}
                                    </Tag>
                                    <div style={{ fontSize: 12 }}>
                                        <div style={{ marginBottom: 4 }}>
                                            🇨🇳 {(fabe.fabe as any)[key]?.zh || '-'}
                                        </div>
                                        <div style={{ color: '#666' }}>
                                            🇺🇸 {(fabe.fabe as any)[key]?.en || '-'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ),
                }]}
            />
        </Card>
    );
};
